import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact, isEqual } from 'lodash-es';
import { netTermsOptions } from '~/constants';
import { approvalMethodNoticePeriodModel, paymentMethodModel, relationshipModel, serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { ServiceChangeItemType } from '~/models/service/model';
import { ServiceCostType } from '~/models/service-cost/model';
import { formatDate } from '~/models/time';
import { utils } from '~/utils';
import { preapprovalPeriodWord } from '~/utils/billing-period-text';
export const amendmentsTextsPath = 'amendments';
export function netTermsSelectOptions(texts) {
    return netTermsOptions.map((numOfDays)=>({
            value: numOfDays,
            text: numOfDays === 0 ? texts.netTerms.onAcceptance() : texts.netTerms.days({
                count: numOfDays
            })
        }));
}
export function getAllowManualPaymentsChangeBullet(params) {
    const { relationship, allowManualPayments, texts } = params;
    return allowManualPayments === relationship.allowManualPayments ? null : {
        text: getAllowManualPaymentsChangeText({
            origValue: relationship.allowManualPayments,
            newValue: allowManualPayments,
            texts
        }),
        requiresApproval: !allowManualPayments
    };
}
export function getAllowManualPaymentsChangeText(params) {
    const { origValue, newValue, texts } = params;
    const from = getAllowManualPaymentsText({
        allowManualPayments: origValue,
        texts
    });
    const to = getAllowManualPaymentsText({
        allowManualPayments: newValue,
        texts
    });
    return texts.manualApproval.changed({
        from,
        to
    });
}
export function getAllowManualPaymentsText(params) {
    const { allowManualPayments, texts } = params;
    return allowManualPayments ? texts.manualApproval.required() : texts.manualApproval.notRequired();
}
export function getCoverCCFeesChangeBullet(params) {
    const { relationship, coverCreditCardFees, texts } = params;
    const origCoverCCFees = relationshipModel.isVendorCoveringCCFee(relationship);
    const isCreditCard = !!relationship.paymentMethod && paymentMethodModel.isCreditCard(relationship.paymentMethod);
    if (coverCreditCardFees === origCoverCCFees) {
        return null;
    } else {
        const vendorBusinessName = relationshipModel.vendorName(relationship);
        const clientBusinessName = relationshipModel.customerName(relationship);
        return {
            text: getCoverCCFeesChangeText({
                oldCoverCCFees: origCoverCCFees,
                newCoverCCFees: coverCreditCardFees,
                vendorBusinessName,
                clientBusinessName,
                texts
            }),
            requiresApproval: isCreditCard && !coverCreditCardFees
        };
    }
}
export function getCoverCCFeesChangeText(params) {
    const { oldCoverCCFees, newCoverCCFees, vendorBusinessName, clientBusinessName, texts } = params;
    const from = getCoverCCFeesText({
        coverCreditCardFees: oldCoverCCFees,
        vendorBusinessName,
        clientBusinessName,
        texts
    });
    const to = getCoverCCFeesText({
        coverCreditCardFees: newCoverCCFees,
        vendorBusinessName,
        clientBusinessName,
        texts
    });
    return texts.coverCCFees.changed({
        from,
        to
    });
}
export function getCoverCCFeesText(params) {
    const { coverCreditCardFees, vendorBusinessName, clientBusinessName, texts } = params;
    const name = coverCreditCardFees ? vendorBusinessName || texts.coverCCFees.defaultServiceProvider() : clientBusinessName || texts.coverCCFees.defaultClient();
    return texts.coverCCFees.paidBy({
        name
    });
}
export function getNetTermsChangeBullet(params) {
    const { relationship, updatedNetTerms, texts } = params;
    if (updatedNetTerms === relationship.netTerms) {
        return null;
    }
    const options = netTermsSelectOptions(texts);
    const beforeText = options.find((opt)=>opt.value === relationship.netTerms).text;
    const afterText = options.find((opt)=>opt.value === updatedNetTerms).text;
    return {
        text: texts.paymentTerms.changed({
            before: beforeText,
            after: afterText
        }),
        requiresApproval: updatedNetTerms < relationship.netTerms
    };
}
export function getInvoicesApprovalMethodChangeBullet(params) {
    const { originalApprovalMethod, newApprovalMethod, texts } = params;
    const originalNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(originalApprovalMethod);
    const newNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(newApprovalMethod);
    if (originalNoticePeriod != null) {
        if (newNoticePeriod != null) {
            return newNoticePeriod !== originalNoticePeriod ? {
                text: texts.outOfScopeApproval.noticePeriodChanged({
                    from: texts.netTerms.days({
                        count: originalNoticePeriod
                    }),
                    to: texts.netTerms.days({
                        count: newNoticePeriod
                    })
                }),
                requiresApproval: newNoticePeriod < originalNoticePeriod
            } : null;
        } else {
            return {
                text: texts.outOfScopeApproval.changedToManual(),
                requiresApproval: false
            };
        }
    } else {
        if (newNoticePeriod != null) {
            return {
                text: texts.outOfScopeApproval.changedToAuto({
                    period: texts.netTerms.days({
                        count: newNoticePeriod
                    })
                }),
                requiresApproval: true
            };
        } else {
            return null;
        }
    }
}
export function getAmendmentsApprovalMethodChangeBullet(params) {
    const { originalApprovalMethod, newApprovalMethod, texts } = params;
    const originalNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(originalApprovalMethod);
    const newNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(newApprovalMethod);
    if (originalNoticePeriod != null) {
        if (newNoticePeriod != null) {
            return newNoticePeriod !== originalNoticePeriod ? {
                text: texts.amendmentsApproval.noticePeriodChanged({
                    from: texts.netTerms.days({
                        count: originalNoticePeriod
                    }),
                    to: texts.netTerms.days({
                        count: newNoticePeriod
                    })
                }),
                requiresApproval: newNoticePeriod < originalNoticePeriod
            } : null;
        } else {
            return {
                text: texts.amendmentsApproval.changedToManual(),
                requiresApproval: false
            };
        }
    } else {
        if (newNoticePeriod != null) {
            return {
                text: texts.amendmentsApproval.changedToAuto({
                    period: texts.netTerms.days({
                        count: newNoticePeriod
                    })
                }),
                requiresApproval: true
            };
        } else {
            return null;
        }
    }
}
export function getServiceChangeBullets(params) {
    const { service, SERVICE_TEMPLATE_BUNDLE, texts } = params;
    if (serviceModel.isAwaitingApproval(service.status)) {
        return [
            {
                text: SERVICE_TEMPLATE_BUNDLE ? texts.service.serviceAddedBundle({
                    name: service.name
                }) : texts.service.newServiceAdded(),
                requiresApproval: true
            }
        ];
    }
    return serviceModel.makeServiceChangeList(service).map((item)=>{
        switch(item.type){
            case ServiceChangeItemType.Description:
                {
                    return {
                        text: SERVICE_TEMPLATE_BUNDLE ? texts.service.descriptionChangedBundle() : texts.service.descriptionChanged(),
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.IncludedServicesDescription:
                {
                    return {
                        text: texts.service.includedServicesDescriptionChanged(),
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.Discount:
                {
                    var _service_cost_discount, _item_value;
                    const noDiscount = texts.service.noDiscount();
                    const fromDiscount = ((_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.format()) || noDiscount;
                    const toDiscount = ((_item_value = item.value) === null || _item_value === void 0 ? void 0 : _item_value.format()) || noDiscount;
                    return {
                        text: texts.service.discountChanged({
                            from: fromDiscount,
                            to: toDiscount
                        }),
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.BillingTrigger:
                {
                    const oldBillingCycle = getBillingCycle({
                        billing: service.billing,
                        texts
                    });
                    const newBillingCycle = getBillingCycle({
                        billing: item.value,
                        texts
                    });
                    const oldBillingMode = getBillingMode({
                        billing: service.billing,
                        texts
                    });
                    const newBillingMode = getBillingMode({
                        billing: item.value,
                        texts
                    });
                    const oldBillingDayOfMonth = serviceModel.billingDayOfMonth(service);
                    const newBillingDayOfMonth = serviceBillingModel.billingDayOfMonth(item.value);
                    const oldBillingDayOfWeek = serviceModel.billingDayOfWeek(service);
                    const newBillingDayOfWeek = serviceBillingModel.billingDayOfWeek(item.value);
                    return compact([
                        oldBillingCycle !== newBillingCycle && {
                            text: texts.service.billingCycleChanged({
                                from: oldBillingCycle,
                                to: newBillingCycle
                            }),
                            requiresApproval: item.requiresApproval
                        },
                        oldBillingMode !== newBillingMode && {
                            text: texts.service.billingTriggerChanged({
                                from: oldBillingMode,
                                to: newBillingMode
                            }),
                            requiresApproval: false,
                            emailWontBeSent: true
                        },
                        oldBillingDayOfMonth != null && newBillingDayOfMonth != null && !isEqual(oldBillingDayOfMonth, newBillingDayOfMonth) && {
                            text: texts.service.billingDayChanged({
                                from: oldBillingDayOfMonth.formatDay(),
                                to: newBillingDayOfMonth.formatDay()
                            }),
                            requiresApproval: false
                        },
                        oldBillingDayOfWeek != null && newBillingDayOfWeek != null && oldBillingDayOfWeek !== newBillingDayOfWeek && {
                            text: texts.service.billingDayChanged({
                                from: oldBillingDayOfWeek,
                                to: newBillingDayOfWeek
                            }),
                            requiresApproval: false
                        }
                    ]);
                }
            case ServiceChangeItemType.Pricing:
                {
                    const amendedBilling = serviceModel.isAmended(service.status) ? service.status.amended.changes.billing : undefined;
                    const originalPrice = getPrice({
                        cost: service.cost,
                        billing: service.billing,
                        texts
                    });
                    const newPrice = getPrice({
                        cost: _object_spread_props(_object_spread({}, item.value), {
                            discount: null
                        }),
                        billing: amendedBilling || service.billing,
                        texts
                    });
                    return compact([
                        originalPrice !== newPrice && {
                            text: texts.service.priceChanged({
                                from: originalPrice,
                                to: newPrice
                            }),
                            requiresApproval: item.requiresApproval
                        }
                    ]);
                }
            case ServiceChangeItemType.PriceIncrease:
                {
                    const prevPriceIncrease = serviceModel.priceIncrease(service);
                    const text = getPriceIncreaseBulletText({
                        prevPriceIncrease,
                        itemValue: item.value,
                        texts
                    });
                    return {
                        text,
                        requiresApproval: item.requiresApproval
                    };
                }
        }
    }).flat(1);
}
function getPriceIncreaseBulletText(params) {
    const { prevPriceIncrease, itemValue, texts } = params;
    if (prevPriceIncrease.exists()) {
        if (itemValue.exists()) {
            return texts.priceIncrease.changed({
                from: getPriceIncreaseText({
                    priceIncrease: prevPriceIncrease,
                    texts
                }),
                to: getPriceIncreaseText({
                    priceIncrease: itemValue,
                    texts
                })
            });
        }
        return texts.priceIncrease.removed({
            value: getPriceIncreaseText({
                priceIncrease: prevPriceIncrease,
                texts
            })
        });
    }
    if (itemValue.exists()) {
        return texts.priceIncrease.added({
            value: getPriceIncreaseText({
                priceIncrease: itemValue,
                texts
            })
        });
    }
    return '';
}
function getPriceIncreaseText(params) {
    const { priceIncrease, texts } = params;
    const increaseOn = priceIncrease.increaseOn();
    const percentIncrease = priceIncrease.percentIncrease();
    const percent = serviceModel.isInvalidPercentIncrease(percentIncrease) ? '' : String(percentIncrease);
    return increaseOn ? texts.priceIncrease.withDate({
        percent,
        date: formatDate(increaseOn)
    }) : texts.priceIncrease.withoutDate({
        percent
    });
}
function getBillingCycle(params) {
    const { billing, texts } = params;
    return serviceBillingModel.isRecurring(billing) ? serviceModel.recurrencePeriod(billing) : texts.billingCycle.oneTime();
}
function getBillingMode(params) {
    const { billing, texts } = params;
    return serviceBillingModel.isAutomatic(billing) ? texts.billingCycle.automatic() : texts.billingCycle.manual();
}
function getPrice(params) {
    const { cost, billing, texts } = params;
    switch(cost.pricingType){
        case ServiceCostType.Fixed:
            return utils.formatDollarAmount(cost.price);
        case ServiceCostType.Range:
            return texts.price.range({
                min: utils.formatDollarAmount(cost.minPrice || 0),
                max: utils.formatDollarAmount(cost.maxPrice)
            });
        case ServiceCostType.Variable:
            {
                const pricePerUnit = "".concat(utils.formatDollarAmount(cost.price), "/").concat(serviceCostModel.formattedUnitName(cost));
                if (!cost.unitCap) return pricePerUnit;
                const count = utils.formatNumberWithCommas(cost.unitCap);
                const period = serviceBillingModel.isOnDemand(billing) ? preapprovalPeriodWord(billing.preapprovalPeriod) || null : null;
                const preapprovedText = period ? texts.price.preapprovedWithPeriod({
                    count,
                    period
                }) : texts.price.preapproved({
                    count
                });
                return "".concat(pricePerUnit, " ").concat(preapprovedText);
            }
    }
}
